import React, { forwardRef, Children, useMemo } from 'react'
import PropTypes from 'prop-types'
import { css } from '@linaria/core'
import { sanitizeProps, Observable } from '@fs/zion-frontend-friends'
import Ghost from '../ghost'
import ListContext from './ListContext'

const listCss = css`
  scroll-behavior: smooth;
  /* allow drag to continue scrolling after user lets go for mobile (iOS Safari) */
  -webkit-overflow-scrolling: touch;
`

const listItemPartitions = (listItems) => {
  const listItemsCount = 100
  return listItems.reduce((partitions, listItem, index) => {
    const needsNewPartition = index % listItemsCount === 0
    if (needsNewPartition) partitions.push([])
    const partition = partitions[partitions.length - 1]
    partition.push(listItem)
    return partitions
  }, [])
}

const List = forwardRef(
  (
    {
      maxHeight,
      children,
      ghosted,
      bleedToContainer,

      /** [HIDDEN]: For internal Zion-UI use only */
      as: TagName = 'ul', // DO NOT USE THIS unless you _rely_ on overriding accessibility protocols (like we have to do in Autosuggest)

      ...props
    },
    ref
  ) => {
    const contextValue = useMemo(() => ({ bleedToContainer }), [bleedToContainer])

    if (ghosted) {
      const losNiños = Children.toArray(children)
      const partitions = listItemPartitions(losNiños)

      return (
        <TagName
          {...sanitizeProps(props)}
          className={listCss}
          ref={ref}
          style={maxHeight ? { maxHeight, overflow: 'auto' } : {}}
        >
          <ListContext.Provider value={contextValue}>
            <Observable rootMargin="200px">
              {partitions.map((partition, index) => (
                // This was a conscious decision because we unconsciously don't know what else to do...
                // eslint-disable-next-line react/no-array-index-key -- see above
                <Ghost key={index} heightEstimate={partition.length * 50}>
                  {partition.map((item, idx) => (
                    // See above note
                    // eslint-disable-next-line react/no-array-index-key -- see above
                    <Ghost key={idx} heightEstimate={50}>
                      {item}
                    </Ghost>
                  ))}
                </Ghost>
              ))}
            </Observable>
          </ListContext.Provider>
        </TagName>
      )
    }

    return (
      <TagName
        {...sanitizeProps(props)}
        className={listCss}
        ref={ref}
        style={maxHeight ? { maxHeight, overflow: 'auto' } : {}}
      >
        <ListContext.Provider value={contextValue}>{children}</ListContext.Provider>
      </TagName>
    )
  }
)

List.propTypes = {
  /** Children must be set of ListItems for accessibility to work properly */
  children: PropTypes.node,
  /** In certain situations, we need to have a list scroll inside of a finite space (like AutoSuggest or MenuOverlay). Set this if you need the keyboard navigation to auto-scroll to items with a tab index. */
  maxHeight: PropTypes.string,
  /** 👻 If you have a large List, you gonna wanna set dis...trus' me. */
  ghosted: PropTypes.bool,
  /** When set, the hover state of list items will extend to the edges of their container */
  bleedToContainer: PropTypes.bool,
}

export default List
